import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { tradingAPI, userAPI, notificationsAPI } from '../../services/api';
import { MOBILE_COLORS, MOBILE_SPACING, MOBILE_BORDERS, MOBILE_TYPOGRAPHY, MOBILE_ANIMATIONS } from '../../styles/mobileTheme';
import MobileCard from './MobileCard';
import MobileButton from './MobileButton';
import MobilePriceDisplay from './MobilePriceDisplay';
import MobilePercentBadge from './MobilePercentBadge';
import {
  Eye, EyeOff, ArrowUpRight, ArrowDownLeft, RefreshCw, Send,
  TrendingUp, TrendingDown, Wallet, BarChart3, Rocket, Star,
  ChevronRight, Bell, Settings, Flame, Zap, Activity, Clock,
  Globe, AlertCircle, ExternalLink
} from 'lucide-react';

const MobileHomePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // State
  const [dashboardData, setDashboardData] = useState(null);
  const [marketData, setMarketData] = useState({ gainers: [], losers: [], trending: [] });
  const [marketNews, setMarketNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hideBalance, setHideBalance] = useState(false);
  const [activeMarketTab, setActiveMarketTab] = useState('gainers');
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Load notifications count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadNotifications(res.data?.count || 0);
      } catch (err) {}
    };
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [dashboardRes, gainersRes, trendingRes, newsRes] = await Promise.all([
        tradingAPI.getDashboardBalances().catch(() => null),
        tradingAPI.getGainersLosers(5).catch(() => null),
        tradingAPI.getTrending().catch(() => null),
        tradingAPI.getMarketNews().catch(() => null),
      ]);

      if (dashboardRes?.data) setDashboardData(dashboardRes.data);
      if (gainersRes?.data || trendingRes?.data) {
        setMarketData({
          gainers: gainersRes?.data?.gainers || [],
          losers: gainersRes?.data?.losers || [],
          trending: trendingRes?.data?.trending || [],
        });
      }
      if (newsRes?.data?.news) setMarketNews(newsRes.data.news);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  // Calculations
  const totalBalance = dashboardData?.total_balance || 0;
  const totalPnL = dashboardData?.total_pnl || 0;
  const pnlPercent = totalBalance > 0 ? (totalPnL / totalBalance * 100) : 0;
  const exchangeBalance = dashboardData?.exchange_balance || 0;
  const spotBalance = dashboardData?.spot_balance || 0;
  const idoBalance = dashboardData?.ido_balance || 0;
  const marginInPositions = dashboardData?.margin_in_positions || 0;

  // Format currency
  const formatCurrency = (val) => {
    if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${val.toFixed(2)}`;
  };

  // Quick actions - simplified to 2 main actions
  const quickActions = [
    { icon: BarChart3, label: 'Торговля', path: '/trading', color: MOBILE_COLORS.primary },
    { icon: Wallet, label: 'Кошелёк', path: '/wallet', color: MOBILE_COLORS.success },
  ];

  // Market tabs - only gainers and losers (trending doesn't have price data)
  const marketTabs = [
    { id: 'gainers', label: 'Лидеры роста', icon: TrendingUp },
    { id: 'losers', label: 'Лидеры падения', icon: TrendingDown },
  ];

  const getMarketList = () => {
    switch (activeMarketTab) {
      case 'losers': return marketData.losers;
      case 'gainers': 
      default: return marketData.gainers;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: MOBILE_COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${MOBILE_COLORS.border}`,
          borderTopColor: MOBILE_COLORS.primary,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: MOBILE_COLORS.background,
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{
        padding: `${MOBILE_SPACING.lg} ${MOBILE_SPACING.lg}`,
        paddingTop: 'max(16px, env(safe-area-inset-top))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.md }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: MOBILE_BORDERS.radius.full,
            background: MOBILE_COLORS.gradientPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '16px',
            color: '#0b0e11',
          }}>
            {user?.name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '12px' }}>Добро пожаловать</div>
            <div style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '15px' }}>
              {user?.name || user?.email?.split('@')[0] || 'Трейдер'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: MOBILE_SPACING.sm }}>
          <button
            onClick={handleRefresh}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: MOBILE_COLORS.backgroundSecondary,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <RefreshCw 
              size={18} 
              color={MOBILE_COLORS.textSecondary}
              style={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
              }} 
            />
          </button>
          <Link to="/notifications" style={{ textDecoration: 'none', position: 'relative' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: MOBILE_COLORS.backgroundSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Bell size={18} color={MOBILE_COLORS.textSecondary} />
            </div>
            {unreadNotifications > 0 && (
              <div style={{
                position: 'absolute',
                top: -2,
                right: -2,
                minWidth: 16,
                height: 16,
                borderRadius: 8,
                background: '#f6465d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: '#fff',
                padding: '0 4px',
              }}>
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </div>
            )}
          </Link>
          <Link to="/profile" style={{ textDecoration: 'none' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: MOBILE_COLORS.backgroundSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Settings size={18} color={MOBILE_COLORS.textSecondary} />
            </div>
          </Link>
        </div>
      </div>

      {/* Main Balance Card */}
      <div style={{ padding: `0 ${MOBILE_SPACING.lg}`, marginBottom: MOBILE_SPACING.xl }}>
        <MobileCard variant="elevated" style={{
          background: 'linear-gradient(135deg, #1e2329 0%, #2b3139 50%, #1e2329 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative elements */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            background: 'radial-gradient(circle, rgba(240,185,11,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Balance Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: MOBILE_SPACING.md,
            }}>
              <span style={{ color: MOBILE_COLORS.textSecondary, fontSize: '13px' }}>
                Общий баланс
              </span>
              <button
                onClick={() => setHideBalance(!hideBalance)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                }}
              >
                {hideBalance 
                  ? <EyeOff size={18} color={MOBILE_COLORS.textSecondary} />
                  : <Eye size={18} color={MOBILE_COLORS.textSecondary} />
                }
              </button>
            </div>

            {/* Main Balance */}
            <div style={{ marginBottom: MOBILE_SPACING.md }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '700',
                color: MOBILE_COLORS.textPrimary,
                fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono,
                letterSpacing: '-0.02em',
              }}>
                {hideBalance ? '••••••••' : formatCurrency(totalBalance)}
              </div>
            </div>

            {/* PnL Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: totalPnL >= 0 ? MOBILE_COLORS.successMuted : MOBILE_COLORS.dangerMuted,
            }}>
              {totalPnL >= 0 
                ? <TrendingUp size={14} color={MOBILE_COLORS.success} />
                : <TrendingDown size={14} color={MOBILE_COLORS.danger} />
              }
              <span style={{
                color: totalPnL >= 0 ? MOBILE_COLORS.success : MOBILE_COLORS.danger,
                fontWeight: '600',
                fontSize: '13px',
              }}>
                {hideBalance ? '••••' : `${totalPnL >= 0 ? '+' : ''}${formatCurrency(totalPnL)} (${pnlPercent >= 0 ? '+' : ''}${pnlPercent.toFixed(2)}%)`}
              </span>
            </div>

            {/* Balance Breakdown */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: MOBILE_SPACING.md,
              marginTop: MOBILE_SPACING.lg,
              paddingTop: MOBILE_SPACING.lg,
              borderTop: `1px solid ${MOBILE_COLORS.border}`,
            }}>
              <div>
                <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                  Доступно
                </div>
                <div style={{ color: MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
                  {hideBalance ? '••••' : formatCurrency(exchangeBalance)}
                </div>
              </div>
              <div>
                <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                  В позициях
                </div>
                <div style={{ color: marginInPositions > 0 ? MOBILE_COLORS.warning : MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
                  {hideBalance ? '••••' : formatCurrency(marginInPositions)}
                </div>
              </div>
              <div>
                <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                  Спот
                </div>
                <div style={{ color: MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
                  {hideBalance ? '••••' : formatCurrency(spotBalance)}
                </div>
              </div>
              <div>
                <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                  IDO
                </div>
                <div style={{ color: MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
                  {hideBalance ? '••••' : formatCurrency(idoBalance)}
                </div>
              </div>
            </div>
          </div>
        </MobileCard>
      </div>

      {/* Quick Actions - Two main buttons */}
      <div style={{ padding: `0 ${MOBILE_SPACING.lg}`, marginBottom: MOBILE_SPACING.xl }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: MOBILE_SPACING.md,
        }}>
          {quickActions.map((action, i) => (
            <Link key={i} to={action.path} style={{ textDecoration: 'none' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: MOBILE_SPACING.sm,
                padding: '16px 20px',
                borderRadius: MOBILE_BORDERS.radius.lg,
                backgroundColor: i === 0 ? MOBILE_COLORS.primary : MOBILE_COLORS.backgroundSecondary,
                border: i === 0 ? 'none' : `1px solid ${MOBILE_COLORS.border}`,
              }}>
                <action.icon size={20} color={i === 0 ? '#0b0e11' : action.color} />
                <span style={{
                  color: i === 0 ? '#0b0e11' : MOBILE_COLORS.textPrimary,
                  fontSize: '15px',
                  fontWeight: '700',
                }}>
                  {action.label}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Open Positions Summary */}
      {dashboardData?.positions_count > 0 && (
        <div style={{ padding: `0 ${MOBILE_SPACING.lg}`, marginBottom: MOBILE_SPACING.xl }}>
          <Link to="/trading" style={{ textDecoration: 'none' }}>
            <MobileCard style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: `linear-gradient(90deg, ${MOBILE_COLORS.primaryMuted} 0%, ${MOBILE_COLORS.card} 100%)`,
              borderColor: MOBILE_COLORS.primary,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.md }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: MOBILE_BORDERS.radius.md,
                  backgroundColor: MOBILE_COLORS.primaryMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Activity size={22} color={MOBILE_COLORS.primary} />
                </div>
                <div>
                  <div style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '14px' }}>
                    {dashboardData.positions_count} активных позиций
                  </div>
                  <div style={{
                    color: dashboardData.open_pnl >= 0 ? MOBILE_COLORS.success : MOBILE_COLORS.danger,
                    fontSize: '13px',
                    fontWeight: '600',
                  }}>
                    PnL: {dashboardData.open_pnl >= 0 ? '+' : ''}{formatCurrency(dashboardData.open_pnl)}
                  </div>
                </div>
              </div>
              <ChevronRight size={20} color={MOBILE_COLORS.textSecondary} />
            </MobileCard>
          </Link>
        </div>
      )}

      {/* Market Section */}
      <div style={{ padding: `0 ${MOBILE_SPACING.lg}` }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: MOBILE_SPACING.md,
        }}>
          <h2 style={{
            color: MOBILE_COLORS.textPrimary,
            fontSize: '18px',
            fontWeight: '700',
            margin: 0,
          }}>
            Рынок
          </h2>
          <Link to="/trading" style={{
            color: MOBILE_COLORS.primary,
            fontSize: '13px',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            Все <ChevronRight size={16} />
          </Link>
        </div>

        {/* Market Tabs */}
        <div style={{
          display: 'flex',
          gap: MOBILE_SPACING.sm,
          marginBottom: MOBILE_SPACING.lg,
          overflowX: 'auto',
          paddingBottom: MOBILE_SPACING.xs,
        }}>
          {marketTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveMarketTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: MOBILE_BORDERS.radius.full,
                border: 'none',
                backgroundColor: activeMarketTab === tab.id 
                  ? MOBILE_COLORS.primaryMuted 
                  : MOBILE_COLORS.backgroundSecondary,
                color: activeMarketTab === tab.id 
                  ? MOBILE_COLORS.primary 
                  : MOBILE_COLORS.textSecondary,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: MOBILE_ANIMATIONS.fast,
              }}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Market List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: MOBILE_SPACING.sm }}>
          {getMarketList().slice(0, 5).map((coin, i) => (
            <Link 
              key={i} 
              to={`/trading?symbol=${coin.symbol?.toUpperCase() || coin.id?.toUpperCase()}USDT`}
              style={{ textDecoration: 'none' }}
            >
              <MobileCard padding="md" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.md }}>
                  <div style={{ position: 'relative' }}>
                    {coin.image ? (
                      <img
                        src={coin.image}
                        alt={coin.symbol}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: MOBILE_BORDERS.radius.full,
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: MOBILE_BORDERS.radius.full,
                        backgroundColor: MOBILE_COLORS.backgroundTertiary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: MOBILE_COLORS.textPrimary,
                      }}>
                        {(coin.symbol || coin.id)?.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    {i < 3 && (
                      <div style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        width: '18px',
                        height: '18px',
                        borderRadius: MOBILE_BORDERS.radius.full,
                        backgroundColor: MOBILE_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '700',
                        color: '#0b0e11',
                      }}>
                        {i + 1}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{
                      color: MOBILE_COLORS.textPrimary,
                      fontWeight: '600',
                      fontSize: '14px',
                    }}>
                      {(coin.symbol || coin.id)?.toUpperCase()}
                    </div>
                    <div style={{
                      color: MOBILE_COLORS.textSecondary,
                      fontSize: '12px',
                    }}>
                      {coin.name?.slice(0, 15) || 'Unknown'}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    color: MOBILE_COLORS.textPrimary,
                    fontWeight: '600',
                    fontSize: '14px',
                    fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono,
                  }}>
                    ${coin.current_price?.toLocaleString('en-US', { 
                      minimumFractionDigits: coin.current_price < 1 ? 4 : 2,
                      maximumFractionDigits: coin.current_price < 1 ? 6 : 2 
                    }) || '0.00'}
                  </div>
                  <MobilePercentBadge 
                    value={coin.price_change_percentage_24h || 0} 
                    size="sm"
                    showIcon={false}
                  />
                </div>
              </MobileCard>
            </Link>
          ))}
        </div>

        {/* Market News / Events Section */}
        {marketNews.length > 0 && (
          <div style={{ marginTop: MOBILE_SPACING.xl }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: MOBILE_SPACING.md,
            }}>
              <h2 style={{
                color: MOBILE_COLORS.textPrimary,
                fontSize: '16px',
                fontWeight: '700',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Globe size={18} color={MOBILE_COLORS.info} /> Рыночные события
              </h2>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: MOBILE_SPACING.sm }}>
              {marketNews.slice(0, 4).map((news, i) => (
                <Link 
                  key={i} 
                  to={news.coin_id ? `/coin/${news.coin_id}` : '#'}
                  style={{ textDecoration: 'none' }}
                >
                  <MobileCard padding="md" style={{
                    display: 'flex',
                    gap: MOBILE_SPACING.md,
                    cursor: news.coin_id ? 'pointer' : 'default',
                    transition: MOBILE_ANIMATIONS.fast,
                  }}>
                    {/* Coin image or icon */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: MOBILE_BORDERS.radius.full,
                      backgroundColor: MOBILE_COLORS.backgroundTertiary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}>
                      {news.image ? (
                        <img 
                          src={news.image} 
                          alt={news.symbol} 
                          style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                        />
                      ) : news.type === 'price_drop' || news.type === 'flash_move' && news.change_24h < 0 ? (
                        <TrendingDown size={20} color={MOBILE_COLORS.danger} />
                      ) : news.type === 'price_surge' || news.type === 'flash_move' ? (
                        <TrendingUp size={20} color={MOBILE_COLORS.success} />
                      ) : news.type === 'ath_alert' ? (
                        <Star size={20} color={MOBILE_COLORS.primary} />
                      ) : (
                        <Globe size={20} color={MOBILE_COLORS.info} />
                      )}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '4px',
                      }}>
                        {news.symbol && (
                          <span style={{
                            backgroundColor: MOBILE_COLORS.backgroundTertiary,
                            color: MOBILE_COLORS.primary,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '700',
                          }}>
                            {news.symbol}
                          </span>
                        )}
                        {news.change_24h !== undefined && (
                          <span style={{
                            color: news.change_24h >= 0 ? MOBILE_COLORS.success : MOBILE_COLORS.danger,
                            fontSize: '11px',
                            fontWeight: '600',
                          }}>
                            {news.change_24h >= 0 ? '+' : ''}{news.change_24h?.toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <div style={{
                        color: MOBILE_COLORS.textPrimary,
                        fontSize: '13px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {news.title}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        {news.current_price && (
                          <span style={{
                            color: MOBILE_COLORS.textSecondary,
                            fontSize: '11px',
                            fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono,
                          }}>
                            ${news.current_price?.toLocaleString('en-US', { 
                              minimumFractionDigits: news.current_price < 1 ? 4 : 2,
                              maximumFractionDigits: news.current_price < 1 ? 6 : 2 
                            })}
                          </span>
                        )}
                        <span style={{
                          color: MOBILE_COLORS.textMuted,
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          <Clock size={10} /> {news.time || '2ч назад'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Arrow indicator */}
                    {news.coin_id && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: MOBILE_COLORS.textMuted,
                      }}>
                        <ChevronRight size={18} />
                      </div>
                    )}
                  </MobileCard>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* IDO Banner */}
        <Link to="/ido" style={{ textDecoration: 'none' }}>
          <MobileCard style={{
            marginTop: MOBILE_SPACING.xl,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.md }}>
              <Rocket size={28} color="#fff" />
              <div>
                <div style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>
                  IDO Launchpad
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                  Инвестируйте в новые проекты
                </div>
              </div>
            </div>
            <ChevronRight size={24} color="#fff" />
          </MobileCard>
        </Link>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MobileHomePage;
